import type { AxiosRequestConfig } from "axios";
import api from "../utils/api";
import type { AuthUser, Membership, UserSettings } from "../types";

export const AUTH_BOOTSTRAP_TIMEOUT_MS = 15_000;
export type OrganizationAccessStatus = "unknown" | "allowed" | "denied";
export type AuthBootstrapStage = "organizations" | "profile" | "session";

export interface AuthRequestOptions {
  requestTimeoutMs?: number;
  skipAuthRecovery?: boolean;
}

export interface RefreshManageableOrganizationsOptions extends AuthRequestOptions {
  suppressErrors?: boolean;
}

type AuthBootstrapError = {
  code?: unknown;
  message?: unknown;
  response?: { status?: number; data?: { error?: string } };
} | null;

const AUTH_STAGE_FAILURES: Record<
  AuthBootstrapStage,
  { failureMessage: string; timeoutMessage: string }
> = {
  organizations: {
    failureMessage:
      "Signed in, but we could not finish loading your organization access. Please try again.",
    timeoutMessage:
      "Sign-in took too long while finalizing your organization access. Please try again.",
  },
  profile: {
    failureMessage: "Signed in, but we could not load your profile. Please try again.",
    timeoutMessage: "Sign-in took too long while loading your profile. Please try again.",
  },
  session: {
    failureMessage: "Authentication failed",
    timeoutMessage: "Sign-in took too long while verifying your session. Please try again.",
  },
};

export function isTimedOutRequest(error: unknown): boolean {
  return (
    (error as AuthBootstrapError)?.code === "ECONNABORTED" ||
    (error as AuthBootstrapError)?.message === `timeout of ${AUTH_BOOTSTRAP_TIMEOUT_MS}ms exceeded`
  );
}

export function getRequestConfig(
  config: AxiosRequestConfig = {},
  requestTimeoutMs?: number,
): AxiosRequestConfig {
  return requestTimeoutMs === undefined
    ? config
    : { ...config, timeout: config.timeout ?? requestTimeoutMs };
}

export function createTimeoutAwareClient(requestTimeoutMs?: number, skipAuthRecovery = false) {
  return {
    get(url: string, config?: AxiosRequestConfig) {
      return api.get(url, getRequestConfig({ ...config, skipAuthRecovery }, requestTimeoutMs));
    },
  };
}

export function normalizeMemberships(settings: UserSettings | null | undefined): Membership[] {
  if (!Array.isArray(settings?.memberships)) return [];
  return settings.memberships.filter(
    (membership) =>
      membership &&
      typeof membership.organizationId === "string" &&
      membership.organizationId !== "" &&
      typeof membership.organizationName === "string",
  );
}

export function authErrorMessage(stage: AuthBootstrapStage, error: unknown) {
  const failure = AUTH_STAGE_FAILURES[stage];
  if (isTimedOutRequest(error)) {
    return {
      title: "Sign-in is taking longer than expected",
      message: failure.timeoutMessage,
    };
  }
  const requestError = error as AuthBootstrapError;
  return {
    title: "We couldn't complete sign-in",
    message:
      stage === "session"
        ? requestError?.response?.data?.error ||
          (requestError?.message as string | undefined) ||
          failure.failureMessage
        : failure.failureMessage,
  };
}
